import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone } from "lucide-react";

function AppLinksEditor() {
  const [links, setLinks] = useState({ ios_url: "", android_url: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings/app-links").then(r => setLinks(r.data)); }, []);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/settings/app-links", links);
      setLinks(data);
      toast.success("App download links updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setBusy(false);
  };
  return (
    <form onSubmit={save} className="card-flat p-6 space-y-5" data-testid="admin-applinks-editor">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-[#C5A059]/15 text-[#C5A059] flex items-center justify-center"><Smartphone className="w-5 h-5" /></div>
        <div>
          <div className="label-eyebrow text-[#C5A059]">Admin · Site settings</div>
          <h3 className="font-display text-2xl text-[#0A192F]">App download links</h3>
          <p className="text-xs text-slate-500 mt-0.5">Updates the App Store and Google Play badges in the site footer instantly — no redeploy needed.</p>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ios">App Store URL (iOS)</Label>
        <Input id="ios" value={links.ios_url} onChange={e => setLinks({ ...links, ios_url: e.target.value })} placeholder="https://apps.apple.com/us/app/..." className="h-11" data-testid="admin-ios-url-input" />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="android">Google Play URL (Android)</Label>
        <Input id="android" value={links.android_url} onChange={e => setLinks({ ...links, android_url: e.target.value })} placeholder="https://play.google.com/store/apps/details?id=..." className="h-11" data-testid="admin-android-url-input" />
      </div>
      <Button type="submit" disabled={busy} className="bg-[#002855] hover:bg-[#0A192F] text-white rounded-md" data-testid="admin-applinks-save">{busy ? "Saving…" : "Save links"}</Button>
    </form>
  );
}

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ name: "", agency: "", cohort_year: "", bio: "", avatar_url: "" });
  const [posts, setPosts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        agency: user.agency || "",
        cohort_year: user.cohort_year || "",
        bio: user.bio || "",
        avatar_url: user.avatar_url || "",
      });
      api.get(`/posts/user/${user.id}`).then(r => setPosts(r.data));
    }
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/users/me", {
        ...form,
        cohort_year: form.cohort_year ? parseInt(form.cohort_year) : null,
      });
      await refreshUser();
      toast.success("Profile updated");
    } catch {
      toast.error("Failed to update");
    }
    setBusy(false);
  };

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-8" data-testid="profile-page">
      <aside className="md:col-span-1">
        <div className="card-flat p-6">
          <div className="w-20 h-20 rounded-full bg-[#002855] text-[#C5A059] font-display text-3xl flex items-center justify-center">{user.name?.[0]}</div>
          <h2 className="mt-4 font-display text-2xl text-[#0A192F]">{user.name}</h2>
          <div className="text-sm text-slate-500">{user.agency || "—"}</div>
          <div className="label-eyebrow mt-1">Cohort {user.cohort_year || "—"}</div>
          {user.bio && <p className="mt-4 text-sm text-slate-700 leading-relaxed">{user.bio}</p>}
        </div>
        <div className="card-flat p-6 mt-4">
          <div className="label-eyebrow">Activity</div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-center">
            <div><div className="font-display text-2xl text-[#0A192F]">{posts.length}</div><div className="text-xs text-slate-500">Posts</div></div>
            <div><div className="font-display text-2xl text-[#0A192F]">{posts.reduce((s,p) => s + p.likes_count, 0)}</div><div className="text-xs text-slate-500">Likes received</div></div>
          </div>
        </div>
      </aside>
      <section className="md:col-span-2 space-y-8">
        <form onSubmit={save} className="card-flat p-6 space-y-5">
          <div>
            <div className="label-eyebrow">Edit profile</div>
            <h3 className="mt-2 font-display text-2xl text-[#0A192F]">Your member details</h3>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={form.name} onChange={set("name")} data-testid="profile-name-input" className="h-11" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="agency">Agency</Label>
              <Input id="agency" value={form.agency} onChange={set("agency")} data-testid="profile-agency-input" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cohort">Cohort year</Label>
              <Input id="cohort" type="number" value={form.cohort_year} onChange={set("cohort_year")} data-testid="profile-cohort-input" className="h-11" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bio">Bio</Label>
            <Textarea id="bio" value={form.bio} onChange={set("bio")} className="min-h-[100px]" data-testid="profile-bio-input" />
          </div>
          <Button type="submit" disabled={busy} className="bg-[#002855] hover:bg-[#0A192F] text-white rounded-md" data-testid="profile-save-button">{busy ? "Saving…" : "Save changes"}</Button>
        </form>

        {user.role === "admin" && <AppLinksEditor />}

        <div>
          <h3 className="font-display text-2xl text-[#0A192F] mb-4">Your posts</h3>
          <div className="space-y-3">
            {posts.length === 0 && <div className="text-slate-500 text-sm">No posts yet.</div>}
            {posts.map(p => (
              <div key={p.id} className="card-flat p-5">
                <div className="text-sm text-slate-800 whitespace-pre-line">{p.content}</div>
                <div className="mt-3 text-xs text-slate-400">{p.likes_count} likes · {p.comments_count} comments</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
